// Entry point for the `/landing` page (served from landing.html).
//
// This single module does double duty, following the vite-prerender-plugin
// convention:
//   • at BUILD time the plugin imports it and calls the exported `prerender()`
//     to bake the marketing markup into dist/landing.html (great for SEO —
//     Yandex/Google see the real content, not an empty <div>);
//   • in the BROWSER the bottom guard hydrates that prerendered markup.
//
// It is intentionally separate from main.tsx so prerendering doesn't drag in
// the heavy LiveKit/tldraw app or the auth probe.

import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './index.scss';
import './App.scss';

import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter, StaticRouter } from 'react-router';
import { PrimeReactProvider } from 'primereact/api';
import LandingApp from './LandingApp';

// Header/Landing use router hooks (useNavigate / NavLink), so a router context
// is required even though there are no <Routes> here. In the browser we want a
// real BrowserRouter; during prerender (no `window`) we use StaticRouter — the
// SSR-purpose router that doesn't call useLayoutEffect (which would warn in Node).
function Root({ url = '/landing' }: { url?: string }) {
  const tree = (
    <PrimeReactProvider>
      <LandingApp />
    </PrimeReactProvider>
  );

  return typeof window !== 'undefined' ? (
    <BrowserRouter>{tree}</BrowserRouter>
  ) : (
    <StaticRouter location={url}>{tree}</StaticRouter>
  );
}

if (typeof window !== 'undefined') {
  const target = document.getElementById('root') as HTMLElement;
  const app = (
    <StrictMode>
      <Root />
    </StrictMode>
  );
  // In dev there is no prerendered markup to hydrate, so mount fresh.
  if (import.meta.env.DEV) {
    createRoot(target).render(app);
  } else {
    hydrateRoot(target, app);
  }
}

export async function prerender() {
  const { renderToString } = await import('react-dom/server');
  const html = renderToString(<Root url="/landing" />);
  // No `links` returned on purpose — we only want to prerender the landing,
  // not crawl into the CSR app (e.g. the "/" CreateMeeting route).
  return { html };
}
